/**
 * 練習站「指定瓦斯行」（TapPay 商店審核用）。
 * --------------------------------------------------------------------------
 * 練習站網址是送 TapPay 審核的商店網址。審核某家瓦斯行時，網址帶 `?shop=<代號>`，
 * 練習沙盒裡的「練習示範瓦斯行」就換成那家瓦斯行的名稱／統編／電話（與正式站版頭同三欄）。
 *
 * 開關：瓦斯行資料不進程式碼，而是單獨放在 S3 的 `merchants/<代號>.json`
 * （用 scripts/practice-merchant.sh on/off 上傳或刪除）。沒開的瓦斯行根本沒有檔案，
 * bundle 裡也沒有它的資料；代號用亂碼、不放總清單，猜不到別家。
 *
 * 每次載入都重抓一次，檔案刪掉後下一次開頁就恢復假資料（不沿用舊快取）。
 * CloudFront 對不存在的路徑會回 index.html（HTTP 200），所以不能只看狀態碼，
 * 必須是合法 JSON 且有瓦斯行名稱才算數。
 */

export interface PracticeMerchant {
  supplier_name: string;
  tax_id_number: string | null;
  phone: string | null;
}

const SHOP_CODE_KEY = "practiceShopCode";
const MERCHANT_KEY = "practiceMerchant";
/** 代號只收英數與連字號，避免被拿來組出其他路徑。 */
const SHOP_CODE_RE = /^[A-Za-z0-9-]{4,40}$/;
const FETCH_TIMEOUT_MS = 3000;

const DEFAULT_MERCHANT: PracticeMerchant = {
  supplier_name: "練習示範瓦斯行",
  tax_id_number: "12345675",
  phone: "02-2345-6789",
};

const safeGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage 不可用 → 略過（照假資料顯示）
  }
};

const optionalString = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

export const parsePracticeMerchant = (raw: unknown): PracticeMerchant | null => {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const name = optionalString(o.supplier_name);
  if (!name) return null;
  return { supplier_name: name, tax_id_number: optionalString(o.tax_id_number), phone: optionalString(o.phone) };
};

/** 已載入的指定瓦斯行；沒有指定（或檔案已關閉）時為 null。 */
export const getPracticeMerchant = (): PracticeMerchant | null => {
  const raw = safeGet(MERCHANT_KEY);
  if (!raw) return null;
  try {
    return parsePracticeMerchant(JSON.parse(raw));
  } catch {
    return null;
  }
};

const current = (): PracticeMerchant => getPracticeMerchant() ?? DEFAULT_MERCHANT;
export const practiceSupplierName = () => current().supplier_name;
export const practiceSupplierTaxId = () => current().tax_id_number;
export const practiceSupplierPhone = () => current().phone;

/**
 * 開機時呼叫（React 渲染前）：讀網址 `?shop=`（沒帶就沿用本分頁先前的代號），
 * 抓 S3 上的瓦斯行檔案寫進 sessionStorage。任何失敗都退回假資料，不擋開機。
 */
export const loadPracticeMerchant = async (): Promise<void> => {
  let code: string | null = null;
  try {
    code = new URLSearchParams(window.location.search).get("shop");
  } catch {
    code = null;
  }
  if (code !== null) safeSet(SHOP_CODE_KEY, SHOP_CODE_RE.test(code) ? code : null);
  const shop = safeGet(SHOP_CODE_KEY);
  if (!shop || !SHOP_CODE_RE.test(shop)) {
    safeSet(MERCHANT_KEY, null);
    return;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`/merchants/${shop}.json`, { cache: "no-store", signal: controller.signal });
    const merchant = res.ok ? parsePracticeMerchant(await res.json()) : null;
    safeSet(MERCHANT_KEY, merchant ? JSON.stringify(merchant) : null);
  } catch {
    // 404 回的 index.html 會在 res.json() 丟錯；逾時／斷網也一樣 → 視為未開啟
    safeSet(MERCHANT_KEY, null);
  } finally {
    clearTimeout(timer);
  }
};
